import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Stack,
  alpha,
  Dialog,
  Button,
  Divider,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { getDepartmentImageUrl } from 'src/services/department.service';

import Iconify from 'src/components/iconify';

AccessGrantedModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  request: PropTypes.object,
  departments: PropTypes.array,
};

export default function AccessGrantedModal({ open, onClose, request, departments = [] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!request) return null;

  const targetDept = departments.find((d) => d.id === request.targetDepartmentId);
  const hasCustomImage = targetDept?.image === 'HAS_IMAGE';

  // Resolves the dark-masked crop of the department's actual cover photo,
  // or falls back to your beautiful emerald-to-teal gradient.
  const getBackgroundStyle = () => {
    if (hasCustomImage && targetDept) {
      const imageUrl = getDepartmentImageUrl(
        targetDept.id,
        targetDept.updatedAt || targetDept.createdAt
      );
      return {
        background: `linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.75)), url(${imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
    return {
      background: 'linear-gradient(135deg, #00A76F 0%, #005F3D 100%)',
    };
  };

  const handleCreateTicket = () => {
    onClose();
    // Redirects directly to the form-selection page with the department pre-selected and locked
    navigate(
      `/dashboard/newTicket?deptId=${request.targetDepartmentId}&token=${request.token}`
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}
    >
      <DialogTitle sx={{ pb: 1 }} component="div">
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#00A76F' }}>
              {t('tickettt.accessGrantedTitle') || 'Access Granted!'}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
              sx={{ fontWeight: 600 }}
            >
              {t('tickettt.permissionActive') || 'Single-Use Permission Active'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ mr: -1.5 }}>
            <Iconify icon="eva:close-square-fill" width={30} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 1 }}>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {/* Main Success Graphic & Policy Alert */}
          <Box
            sx={{
              p: 2.5,
              borderRadius: '16px',
              bgcolor: (theme) => alpha(theme.palette.success.main, 0.06),
              border: (theme) => `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
              textAlign: 'center',
            }}
          >
            <Stack alignItems="center" spacing={1.5}>
              <Iconify icon="flat-color-icons:ok" width={52} />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 800, color: 'success.darker', lineHeight: 1.5 }}
              >
                {t('tickettt.accessGrantedDesc', {
                  name: request.targetDepartmentName,
                }) ||
                  `Your request to submit a ticket to the ${
                    request.targetDepartmentName || 'target'
                  } department has been approved!`}
              </Typography>
            </Stack>
          </Box>

          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              textAlign: 'center',
              px: 1,
              lineHeight: 1.5,
            }}
          >
            {t('tickettt.accessDurationExplanation') ||
              'This single-use access token will automatically expire 36 hours after approval.'}
          </Typography>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2.5, flexDirection: 'column', gap: 1.5 }}>
        <Button
          fullWidth
          onClick={handleCreateTicket}
          sx={{
            height: 48,
            borderRadius: '12px',
            color: 'white',
            fontWeight: 800,
            textTransform: 'none',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: '0 8px 16px 0 rgba(0,0,0,0.1)',
            transition: 'transform 0.1s ease-in-out',
            '&:hover': {
              transform: 'scale(1.02)',
            },
            ...getBackgroundStyle(),
          }}
        >
          {/* Top Line: Action Label */}
          <Typography
            variant="caption"
            sx={{ fontWeight: 900, fontSize: '0.82rem', lineHeight: 1.1 }}
          >
            {t('tickettt.submitInDept', { name: request.targetDepartmentName }) ||
              `Submit in ${request.targetDepartmentName}`}
          </Typography>

          {/* Bottom Line: Countdown with Clock Icon */}
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
            <Iconify icon="eva:clock-outline" width={13} sx={{ opacity: 0.85 }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 500,
                fontSize: '0.65rem',
                opacity: 0.85,
                lineHeight: 1.1,
              }}
            >
              {t('tickettt.authorizedHoursLeft', { count: 36 }) || 'Authorized: 36h left'}
            </Typography>
          </Stack>
        </Button>

        <Button color="inherit" fullWidth onClick={onClose} sx={{ fontWeight: 700 }}>
          {t('buttons.dismiss') || 'Dismiss'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
